// Home page component using Digital HIG
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore
import DHBox from "@digital-hig/mui/Box";

// Extracted Components
import { ResponsiveContainer } from '../components/common/ResponsiveContainer';
import { HeaderSection } from '../components/home/HeaderSection';
import { MainCard } from '../components/home/MainCard';
import { ToolsSection } from '../components/home/ToolsSection';
import { ResourcesSection } from '../components/home/ResourcesSection';
import Loading from '../components/common/Loading';

// Data
import { toolsData, resourcesData } from '../data/homePageData';

// Authentication
import { useCfpAuthUtilities } from '../providers/CfpAuthContext';

// Utilities
import logger from '../utils/logger';

export default function HomePage() {
  const navigate = useNavigate();
  const { cfpAuthUtils, isAuthenticated, loading } = useCfpAuthUtilities();

  // Check authentication on mount and redirect if not authenticated
  useEffect(() => {
    const checkAuth = async () => {
      // Wait a bit for the auth context to initialize
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check if user is authenticated or has cached user info
      const cachedUserInfo = cfpAuthUtils.getCachedUserInfo?.();
      
      if (!isAuthenticated && !cachedUserInfo) {
        logger.warn('⚠️ HomePage: User not authenticated, redirecting to sign in');
        navigate('/');
      } else {
        logger.info('✅ HomePage: User is authenticated or has cached info');
      }
    };

    // Only check after loading is complete
    if (!loading) {
      checkAuth();
    }
  }, [isAuthenticated, loading, navigate, cfpAuthUtils]);

  // Show loading while authentication is being checked
  if (loading) {
    return (
      <DHBox sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Loading message="Checking authentication..." />
      </DHBox>
    );
  }

  // Don't render the page content until authentication is verified
  if (!isAuthenticated && !cfpAuthUtils.getCachedUserInfo?.()) {
    return (
      <DHBox sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Loading message="Redirecting to sign in..." />
      </DHBox>
    );
  }

  const handleGenerateFile = async () => {
  // Re-validate authentication at action time to catch cross-tab logout
    const valid =  cfpAuthUtils.revalidateAuthentication();
    if (!valid) {
      navigate('/');
      return;
    }
    navigate('/GenerateConfigPage');
  };
  return (
    <ResponsiveContainer>
      {/* Header Section */}
      <HeaderSection />

      {/* Main Content Layout */}
      <DHBox 
        sx={{ 
          display: 'flex', 
          gap: 3, 
          alignItems: 'flex-start', 
          paddingTop: '10px',
          // Digital HIG responsive width
          width: { xs: '100%', sm: '100%', md: '100%', lg: '1296px', xl: '1296px' },
          // Digital HIG responsive height
          height: { xs: 'auto', sm: 'auto', md: 'auto', lg: '671px', xl: '671px' },
          // Digital HIG responsive layout
          flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'row', xl: 'row' },
          // Digital HIG breakpoint for 1320px
          '@media (min-width: 1320px)': {
            width: '1296px',
            height: '671px',
            flexDirection: 'row'
          },
          '@media (max-width: 1319px)': {
            width: '100%',
            height: 'auto',
            flexDirection: 'column',
            gap: { xs: '12px', sm: '16px', md: '16px' }
          }
        }}
      >
        {/* Left side: Main Content */}
        <DHBox 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2,
            // Digital HIG responsive width
            width: { xs: '100%', sm: '100%', md: '100%', lg: '933px', xl: '933px' },
            flex: { xs: 1, sm: 1, md: 1, lg: 'none', xl: 'none' },
            // Digital HIG breakpoint for 1320px
            '@media (min-width: 1320px)': {
              width: '933px'
            },
            '@media (max-width: 1319px)': {
              width: '100%'
            }
          }}
        >
          {/* Main Card */}
          <MainCard onButtonClick={handleGenerateFile} />
        </DHBox>
        
        {/* Right side: Tools and Resources */}
        <DHBox sx={{ 
          // Digital HIG responsive dimensions
          height: { xs: 'auto', sm: 'auto', md: 'auto', lg: '631px', xl: '631px' },
          width: { xs: '100%', sm: '100%', md: '100%', lg: '360px', xl: '360px' },
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 2, md: 2, lg: 0, xl: 0 },
          // Digital HIG breakpoint for 1320px
          '@media (min-width: 1320px)': {
            height: '631px',
            width: '360px'
          },
          '@media (max-width: 1319px)': {
            height: 'auto',
            width: '100%',
            gap: { xs: '12px', sm: '16px', md: '16px' }
          }
        }}>
          {/* Tools Section */}
          <ToolsSection tools={toolsData} />

          {/* Resources Section */}
          <ResourcesSection resources={resourcesData} />
        </DHBox>
      </DHBox>
    </ResponsiveContainer>
  );
}

